import {
  CalendarDetailsVisibility,
  CalendarSetting,
  FirestoreCollections,
  ID,
  LinkedItemType,
  SyncMode,
  Timestamp,
} from '@griply/types';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';

export type SetCalendarSyncOptions<
  TDocRef = unknown,
  TFieldValue = unknown,
  TDb = FirestoreInstance,
> = {
  connectionId: ID;
  linkedItemId: ID;
  linkedItemType: LinkedItemType;
  calendarId: ID;
  mode: SyncMode;
  detailsVisibility?: CalendarDetailsVisibility;
  syncTasksFrom?: Timestamp;
  doc: (db: NoInfer<TDb>, path: string, ...rest: string[]) => TDocRef;
  updateDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void>;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  arrayUnion: (...ids: ID[]) => TFieldValue;
  onWriteError: (error: unknown) => void;
};

export const setCalendarSync = <TDocRef, TFieldValue, TDb = FirestoreInstance>(
  options: SetCalendarSyncOptions<TDocRef, TFieldValue, TDb>,
): void => {
  const {
    connectionId,
    linkedItemId,
    linkedItemType,
    calendarId,
    mode,
    detailsVisibility,
    syncTasksFrom,
    doc,
    updateDoc,
    db,
    serverTimestamp,
    arrayUnion,
    onWriteError,
  } = options;

  const ref = doc(db, FirestoreCollections.ExternalConnections, connectionId);

  const setting: CalendarSetting = {
    linkedItemType,
    calendarId,
    mode,
    ...(detailsVisibility !== undefined && { detailsVisibility }),
    ...(syncTasksFrom !== undefined && { syncTasksFrom }),
  };

  const indexUpdate =
    linkedItemType === LinkedItemType.AllTasks
      ? { 'settings.syncAllTasks': true }
      : {
          [linkedItemType === LinkedItemType.Goal
            ? 'settings.syncedGoals'
            : 'settings.syncedLifeAreas']: arrayUnion(linkedItemId),
        };

  detachWrite(
    updateDoc(ref, {
      [`settings.calendarSettings.${linkedItemId}`]: setting,
      ...indexUpdate,
      updatedAt: serverTimestamp(),
    }),
    onWriteError,
  );
};
